import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getBookingFiles, uploadProcessedFile, downloadFileWithName, getAgencies, getAgencyFiles, cancelBookingFileAssignment } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import DistributeDialog from '../components/DistributeDialog';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DownloadSimple, UploadSimple, CheckCircle, Clock, ArrowRight, FileText, CurrencyInr, PaperPlaneTilt } from '@phosphor-icons/react';
import { X, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AssignedFilesContent() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [agencyAssignedFiles, setAgencyAssignedFiles] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [staffTab, setStaffTab] = useState('active'); // 'active' | 'distributed' | 'pending-upload' | 'uploaded' | 'completed'
  const [tabCounts, setTabCounts] = useState({ active: 0, distributed: 0, 'pending-upload': 0, uploaded: 0, completed: 0 });

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [agencyId, setAgencyId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [source, setSource] = useState('tataneu');

  // Cancel & Reassign state
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [distFile, setDistFile] = useState(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const isStaffRole = user?.role === 'staff' || user?.role === 'sub_staff';
      const filesParams = isStaffRole ? { staff_view: staffTab } : {};
      const [filesRes, agenciesRes, agencyFilesRes] = await Promise.all([
        getBookingFiles(filesParams),
        getAgencies().catch(() => ({ data: [] })),
        getAgencyFiles().catch(() => ({ data: [] }))
      ]);
      setFiles(filesRes.data);
      setAgencies(agenciesRes.data.filter(a => a.status === 'active'));
      setAgencyAssignedFiles(agencyFilesRes.data || []);

      // Fetch counts for all 5 tabs (only for staff)
      if (isStaffRole) {
        const [a, d, p, u, c] = await Promise.all([
          getBookingFiles({ staff_view: 'active' }).catch(() => ({ data: [] })),
          getBookingFiles({ staff_view: 'distributed' }).catch(() => ({ data: [] })),
          getBookingFiles({ staff_view: 'pending-upload' }).catch(() => ({ data: [] })),
          getBookingFiles({ staff_view: 'uploaded' }).catch(() => ({ data: [] })),
          getBookingFiles({ staff_view: 'completed' }).catch(() => ({ data: [] })),
        ]);
        setTabCounts({
          active: a.data.length, distributed: d.data.length,
          'pending-upload': p.data.length,
          uploaded: u.data.length, completed: c.data.length,
        });
      }
    } catch (err) {
      toast.error('Failed to load assigned files');
    } finally {
      setLoading(false);
    }
  }, [user?.role, staffTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-select the distributor's agency in the upload dialog when there's a single match
  useEffect(() => {
    if (!uploadOpen || !selectedFile || agencies.length === 0) return;
    const distAgencyIds = selectedFile.distributor_agency_ids || [];
    const distAgencyNames = (selectedFile.distributor_agency_names || []).map(n => (n || '').toLowerCase());
    let scoped = agencies.filter(a => distAgencyIds.includes(a.id));
    if (scoped.length === 0 && distAgencyNames.length > 0) {
      scoped = agencies.filter(a => distAgencyNames.includes((a.name || '').toLowerCase()));
    }
    if (scoped.length === 1 && !agencyId) {
      setAgencyId(scoped[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadOpen, selectedFile, agencies]);

  const selectedAgency = agencies.find(a => a.id === agencyId);
  const rate = selectedAgency?.rate || 0;
  const qty = parseInt(quantity) || 0;
  const TATANEU_FEE_PERCENT = 2.065;
  const staffFee = source === 'tataneu' ? Math.round(rate * qty * TATANEU_FEE_PERCENT) / 100 : 0;
  const staffVisibleAmount = qty > 0 ? Math.round((rate * qty + staffFee) * 100) / 100 : 0;

  const openUploadDialog = (f) => {
    setSelectedFile(f);
    setUploadFile(null);
    // Pre-fill with existing values when correcting an already-completed file
    const isCorrection = f?.status === 'completed';
    setAgencyId(isCorrection && f?.booking_agency_id ? f.booking_agency_id : '');
    setQuantity(isCorrection && f?.booking_quantity ? String(f.booking_quantity) : '');
    setSource(isCorrection && f?.booking_source ? f.booking_source : 'tataneu');
    setUploadOpen(true);
  };

  const openCancelDialog = (f) => {
    setCancelTarget(f);
    setCancelReason('');
    setCancelOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await cancelBookingFileAssignment(cancelTarget.id, cancelReason.trim());
      if (data.reassigned_to) {
        toast.success(`File ${data.reassigned_to.name} ko forward kar di gayi`);
      } else {
        toast.warning('Koi eligible staff nahi mila — file pending hai. Admin manually assign karega.');
      }
      setCancelOpen(false);
      setCancelTarget(null);
      setCancelReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel fail hua');
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!uploadFile) { toast.error('Report file select karo'); return; }
    if (!agencyId) { toast.error('Agency select karo'); return; }
    if (!qty || qty <= 0) { toast.error('Quantity dalo (1 ya usse zyada)'); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('agency_id', agencyId);
      fd.append('quantity', qty.toString());
      fd.append('source', source);
      const res = await uploadProcessedFile(selectedFile.id, fd);
      toast.success('Report uploaded & booking created successfully!');
      setUploadOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fail hua');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
      assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: Clock },
      processing: { label: 'Processing', color: 'bg-purple-100 text-purple-700', icon: ArrowRight },
      completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    };
    const cfg = map[s] || { label: s, color: 'bg-gray-100 text-gray-700', icon: Clock };
    const Icon = cfg.icon;
    return <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${cfg.color}`}><Icon size={14} weight="fill" /> {cfg.label}</span>;
  };

  const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v || 0);
  const isStaff = user?.role === 'staff';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-bold text-gray-900">Assigned Files</h2>
        {(user?.role === 'staff' || user?.role === 'sub_staff') && (
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 shadow-sm flex-wrap" data-testid="assigned-files-tabs">
            {[
              { id: 'active',         label: 'Active',         icon: Clock,         color: 'blue' },
              { id: 'distributed',    label: 'Distributed',    icon: PaperPlaneTilt, color: 'violet' },
              { id: 'pending-upload', label: 'Report Pending', icon: UploadSimple,  color: 'red' },
              { id: 'uploaded',       label: 'Uploaded',       icon: UploadSimple,  color: 'amber' },
              { id: 'completed',      label: 'Completed',      icon: CheckCircle,   color: 'emerald' },
            ].map(t => {
              const Icon = t.icon;
              const isActive = staffTab === t.id;
              const count = tabCounts[t.id];
              const colorMap = {
                blue: 'bg-blue-600 text-white',
                violet: 'bg-violet-600 text-white',
                red: 'bg-red-600 text-white',
                amber: 'bg-amber-600 text-white',
                emerald: 'bg-emerald-600 text-white',
              };
              const isPendingTab = t.id === 'pending-upload';
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setStaffTab(t.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex items-center gap-1 ${
                    isActive ? colorMap[t.color] : 'text-gray-600 hover:bg-gray-50'
                  } ${isPendingTab && count > 0 && !isActive ? 'ring-1 ring-red-400 animate-pulse' : ''}`}
                  data-testid={`tab-${t.id}-files`}
                >
                  <Icon size={14} weight="fill" /> {t.label}
                  {count > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-white/30 text-white' : `bg-${t.color}-100 text-${t.color}-700`
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Critical transparency banner — booking done but report not uploaded */}
      {(user?.role === 'staff' || user?.role === 'sub_staff') && tabCounts['pending-upload'] > 0 && staffTab !== 'pending-upload' && (
        <div
          className="rounded-lg border-2 border-red-300 bg-gradient-to-r from-red-50 to-amber-50 p-3 flex items-center justify-between gap-3 animate-pulse"
          data-testid="pending-upload-banner"
        >
          <div className="flex items-start gap-2">
            <span className="text-xl">⚠️</span>
            <div>
              <div className="text-sm font-bold text-red-700">
                {tabCounts['pending-upload']} file{tabCounts['pending-upload'] === 1 ? '' : 's'} ka booking complete hai but Report UPLOAD nahi hua!
              </div>
              <div className="text-xs text-red-600 mt-0.5">
                Jab tak report upload nahi hoga, agency wallet debit nahi hoga. 1 click me check karo aur upload karo.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white shrink-0"
            onClick={() => setStaffTab('pending-upload')}
            data-testid="goto-pending-upload-btn"
          >
            View Pending Reports →
          </Button>
        </div>
      )}
      <div className="card-flat overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Distributor</TableHead>
                <TableHead>Oil Company</TableHead>
                <TableHead>Original File</TableHead>
                <TableHead>Instruction</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Report / Booking</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-gray-500">No assigned files</TableCell></TableRow>
              ) : files.map(f => (
                <TableRow key={f.id} data-testid={`staff-bf-${f.id}`}>
                  <TableCell className="text-sm">{formatDate(f.created_at)}</TableCell>
                  <TableCell className="text-sm">
                    {(() => {
                      const agencyNames = f.distributor_agency_names || [];
                      const agencies = f.distributor_agencies || [];
                      const primary = f.booking_agency || agencyNames[0];
                      // Find rate for primary agency
                      const primaryAgency = agencies.find(a => a.name === primary) || agencies[0];
                      const rate = primaryAgency?.rate || 0;
                      const otherAgencies = agencies.filter(a => a.name !== primary && a.rate);
                      if (!primary) {
                        return (
                          <>
                            <span className="font-medium">{f.distributor_name}</span>
                            {rate > 0 && (
                              <div className="text-[11px] text-emerald-700 font-bold mt-0.5" data-testid={`bf-rate-${f.id}`}>
                                Rate: ₹{rate}/cyl
                              </div>
                            )}
                            {otherAgencies.length > 0 && (
                              <div className="text-[10px] text-gray-600 mt-0.5">
                                {otherAgencies.map(a => `${a.name} ₹${a.rate}`).join(' · ')}
                              </div>
                            )}
                          </>
                        );
                      }
                      const more = agencyNames.length > 1 && !f.booking_agency ? ` +${agencyNames.length - 1}` : '';
                      return (
                        <>
                          <div className="font-medium text-gray-900">{primary}{more}</div>
                          <div className="text-[11px] text-gray-500">Owner: {f.distributor_name}</div>
                          {rate > 0 && (
                            <div className="text-[11px] text-emerald-700 font-bold mt-0.5 inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`bf-rate-${f.id}`}>
                              💰 Rate: ₹{rate}/cyl
                            </div>
                          )}
                          {/* Always show other agency rates if multiple agencies linked to distributor */}
                          {otherAgencies.length > 0 && (
                            <div className="text-[10px] text-gray-600 mt-0.5 leading-tight">
                              <span className="text-gray-400">Other:</span> {otherAgencies.map(a => `${a.name.slice(0, 18)} ₹${a.rate}`).join(' · ')}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell><span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{f.oil_company}</span></TableCell>
                  <TableCell>
                    <button onClick={() => downloadFileWithName(`${window.location.origin}/api/booking-files/${f.id}/download`, f.url, f.original_filename)} className="text-blue-600 hover:underline text-sm flex items-center gap-1 cursor-pointer" data-testid={`staff-download-${f.id}`}>
                      <DownloadSimple size={14} /> {f.original_filename}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{f.assignment_instruction || '-'}</TableCell>
                  <TableCell>{statusBadge(f.status)}</TableCell>
                  <TableCell>
                    {f.status === 'completed' ? (
                      <div className="space-y-0.5">
                        <button onClick={() => downloadFileWithName(`${window.location.origin}/api/booking-files/${f.id}/download-processed`, f.processed_file_url, f.processed_file_name)} className="text-blue-600 hover:underline text-xs flex items-center gap-1 cursor-pointer">
                          <FileText size={12} /> {f.processed_file_name}
                        </button>
                        {f.total_bookings_count > 0 && (
                          <p className="text-[10px] text-green-600 font-semibold">{f.total_bookings_count} booking created</p>
                        )}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {(f.status === 'assigned' || f.status === 'processing') && isStaff && (
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Button size="sm" variant="outline" className="text-xs h-7 px-2 gap-1" onClick={() => navigate(`/booking-workspace/${f.id}`)} data-testid={`workspace-${f.id}`} title="Booking Workspace open karo">
                          🪟 Workspace
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 px-2 gap-1 border-blue-300 text-blue-600 hover:bg-blue-50" onClick={() => setDistFile(f)} data-testid={`distribute-${f.id}`} title="Sub-staff me distribute karo">
                          ⚡ Distribute
                        </Button>
                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-xs h-7 px-3 gap-1" onClick={() => openUploadDialog(f)} data-testid={`upload-btn-${f.id}`}>
                          <UploadSimple size={14} /> Upload
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 px-2 gap-1 border-red-300 text-red-600 hover:bg-red-50" onClick={() => openCancelDialog(f)} data-testid={`cancel-btn-${f.id}`} title="Busy / emergency — file ko next staff ko forward karo">
                          <X size={12} /> Cancel
                        </Button>
                      </div>
                    )}
                    {f.status === 'completed' && (
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Done</span>
                        {isStaff && (
                          <Button size="sm" variant="outline" className="text-xs h-7 px-2 gap-1 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => openUploadDialog(f)} data-testid={`correct-btn-${f.id}`} title="Galti se wrong qty? Correct karo — pichla ledger auto-reverse hoga">
                            ✏️ Correct
                          </Button>
                        )}
                        {(f.correction_count || 0) > 0 && (
                          <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold" title={`${f.correction_count} correction(s) by ${f.last_corrected_by_name || ''}`}>↻ {f.correction_count}</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Agency Assigned Files Section */}
      {agencyAssignedFiles.length > 0 && (
        <>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mt-6">
            <PaperPlaneTilt size={20} className="text-[#002FA7]" /> Agency Files
          </h2>
          <div className="card-flat overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Oil Company</TableHead>
                  <TableHead className="text-right">Entries</TableHead>
                  <TableHead>Instruction</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead className="text-right">Bookings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agencyAssignedFiles.map(af => (
                  <TableRow key={`af-${af.id}`} data-testid={`staff-af-${af.id}`}>
                    <TableCell className="text-sm">{af.entry_date}</TableCell>
                    <TableCell className="font-medium text-sm">{af.agency_name}</TableCell>
                    <TableCell><span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{af.oil_company}</span></TableCell>
                    <TableCell className="text-right text-sm">
                      {af.entry_count}
                      {af.duplicate_count > 0 && <span className="text-xs text-orange-500 ml-1">(-{af.duplicate_count} dupes)</span>}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{af.assignment_instruction || '-'}</TableCell>
                    <TableCell>{statusBadge(af.status)}</TableCell>
                    <TableCell>
                      {af.url && (
                        <button onClick={() => downloadFileWithName(`${window.location.origin}/api/agency-files/${af.id}/download`, af.url, af.original_filename)}
                          className="text-blue-600 hover:underline text-xs flex items-center gap-1 cursor-pointer">
                          <DownloadSimple size={14} /> Download
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {af.status === 'completed' ? (
                        <span className="text-xs text-green-600 flex items-center gap-1 justify-end">
                          <CheckCircle size={14} weight="fill" /> {af.total_bookings_count} done
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Upload Report + Booking Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedFile?.status === 'completed' ? (
                <>✏️ <span className="text-amber-700">Correct Booking</span></>
              ) : (
                <>
                  <UploadSimple size={22} className="text-purple-600" />
                  Upload Report & Create Booking
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedFile && (
            <div className="space-y-4 mt-2">
              {selectedFile.status === 'completed' && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900">
                  <div className="font-bold mb-1">⚠️ Correction Mode</div>
                  <div>Pichla booking: <b>{selectedFile.booking_quantity || '?'} cyl</b> @ {selectedFile.booking_agency} (₹{(selectedFile.total_amount_debited || 0).toLocaleString('en-IN')})</div>
                  <div className="mt-1 text-amber-700">Submit karte hi: pichla booking + ledger + sales commission <b>permanent delete</b> ho jayega. Naya entry hi final hoga.</div>
                </div>
              )}              {/* File Info */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-500">Distributor File</p>
                {(() => {
                  const agencyNames = selectedFile.distributor_agency_names || [];
                  const agencies = selectedFile.distributor_agencies || [];
                  const primary = selectedFile.booking_agency || agencyNames[0];
                  const primaryAgency = agencies.find(a => a.name === primary) || agencies[0];
                  const rate = primaryAgency?.rate || 0;
                  return primary ? (
                    <>
                      <p className="text-sm font-medium">{primary} — {selectedFile.oil_company}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">Owner: {selectedFile.distributor_name}</p>
                      {rate > 0 && (
                        <p className="text-xs text-emerald-700 font-bold mt-1 inline-block bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                          💰 Booking Rate: ₹{rate}/cyl
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm font-medium">{selectedFile.distributor_name} — {selectedFile.oil_company}</p>
                  );
                })()}
                <p className="text-xs text-gray-400 mt-0.5">{selectedFile.original_filename}</p>
              </div>

              {/* Report File */}
              <div>
                <Label className="text-sm font-medium">Report File <span className="text-red-500">*</span></Label>
                <div className="mt-1 border-2 border-dashed border-gray-300 p-3 text-center rounded-lg hover:border-purple-400 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv,.txt,.pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="hidden"
                    id="report-file-input"
                    data-testid="report-file-input"
                  />
                  <label htmlFor="report-file-input" className="cursor-pointer">
                    {uploadFile ? (
                      <p className="text-sm font-medium text-purple-600">{uploadFile.name}</p>
                    ) : (
                      <p className="text-sm text-gray-500">Click to select report file</p>
                    )}
                  </label>
                </div>
              </div>

              {/* Agency Select — auto-filtered to this distributor's agencies */}
              <div>
                <Label className="text-sm font-medium">Agency <span className="text-red-500">*</span></Label>
                {(() => {
                  const distAgencyIds = selectedFile?.distributor_agency_ids || [];
                  const distAgencyNames = selectedFile?.distributor_agency_names || [];
                  // Match by ID first; if backend didn't ship IDs (older data), fall back to name match.
                  let scoped = agencies.filter(a => distAgencyIds.includes(a.id));
                  if (scoped.length === 0 && distAgencyNames.length > 0) {
                    const lowered = distAgencyNames.map(n => (n || '').toLowerCase());
                    scoped = agencies.filter(a => lowered.includes((a.name || '').toLowerCase()));
                  }
                  const isScoped = scoped.length > 0;
                  const list = isScoped ? scoped : agencies;
                  return (
                    <>
                      <select
                        value={agencyId}
                        onChange={(e) => setAgencyId(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                        data-testid="report-agency-select"
                      >
                        <option value="">{isScoped ? '-- Select Agency --' : '-- Select Agency --'}</option>
                        {list.map(a => (
                          <option key={a.id} value={a.id}>{a.name} (₹{a.rate}/cyl)</option>
                        ))}
                      </select>
                      {!isScoped && (
                        <p className="text-[11px] text-amber-700 mt-1">
                          ⚠️ Distributor par koi agency mapped nahi.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Quantity */}
              <div>
                <Label className="text-sm font-medium">Quantity (Cylinders) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="e.g., 50"
                  className="mt-1"
                  data-testid="report-quantity-input"
                />
              </div>

              {/* Payment Source */}
              <div>
                <Label className="text-sm font-medium">Payment Source <span className="text-red-500">*</span></Label>
                <div className="flex gap-2 mt-1">
                  {[
                    { value: 'tataneu', label: 'TataNeu', color: 'border-pink-400 bg-pink-50 text-pink-700' },
                    { value: 'amazon', label: 'Amazon', color: 'border-orange-400 bg-orange-50 text-orange-700' },
                    { value: 'cash', label: 'Cash', color: 'border-gray-400 bg-gray-50 text-gray-700' },
                  ].map(s => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setSource(s.value)}
                      className={`flex-1 py-2 px-3 text-sm font-medium border-2 rounded-md transition-all ${
                        source === s.value ? s.color + ' ring-1 ring-offset-1' : 'border-gray-200 text-gray-400'
                      }`}
                      data-testid={`report-source-${s.value}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount Preview */}
              {qty > 0 && agencyId && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="amount-preview">
                  <div className="flex items-center gap-2 mb-1">
                    <CurrencyInr size={16} className="text-blue-600" />
                    <span className="text-xs font-semibold text-blue-600 uppercase">Amount Summary</span>
                  </div>
                  <div className="text-sm space-y-0.5">
                    <div className="flex justify-between"><span className="text-gray-600">Rate</span><span className="font-mono">₹{rate} × {qty} = {fmt(rate * qty)}</span></div>
                    {source === 'tataneu' && <div className="flex justify-between"><span className="text-gray-600">TataNeu Fee (~{TATANEU_FEE_PERCENT}%)</span><span className="font-mono text-pink-600">+ {fmt(staffFee)}</span></div>}
                    <div className="flex justify-between border-t pt-1 mt-1 font-bold"><span>Total Amount</span><span className="text-blue-700 font-mono">{fmt(staffVisibleAmount)}</span></div>
                  </div>
                </div>
              )}

              {/* Submit */}
              <Button
                onClick={handleSubmitReport}
                disabled={submitting || !uploadFile || !agencyId || qty <= 0}
                className={`w-full text-white h-11 font-semibold ${selectedFile?.status === 'completed' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                data-testid="submit-report-btn"
              >
                {submitting ? 'Processing...'
                  : selectedFile?.status === 'completed'
                    ? `✏️ Save Correction & Reverse Old Entry${staffVisibleAmount > 0 ? ` (${fmt(staffVisibleAmount)})` : ''}`
                    : `Submit Report & Create Booking${staffVisibleAmount > 0 ? ` (${fmt(staffVisibleAmount)})` : ''}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel & Reassign Dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ArrowsClockwise size={20} weight="bold" /> Cancel & Reassign
            </DialogTitle>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-4 mt-1">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                Aap is file ko reject kar rahe ho — system turant <b>next available staff</b> ko forward kar dega. Ye file aapko wapas nahi milegi (is round mein).
              </div>
              <div className="p-3 bg-gray-50 border rounded">
                <div className="text-xs text-gray-500">File</div>
                <div className="text-sm font-medium">{cancelTarget.original_filename}</div>
                <div className="text-[11px] text-gray-500 mt-1">
                  {(cancelTarget.distributor_agency_names && cancelTarget.distributor_agency_names[0]) || cancelTarget.distributor_name} · {cancelTarget.oil_company}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Reason (optional)</Label>
                <Input
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g., Emergency / busy / out for delivery"
                  className="mt-1"
                  data-testid="cancel-reason-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCancelOpen(false)}
                  disabled={cancelling}
                  className="flex-1"
                  data-testid="cancel-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={handleConfirmCancel}
                  disabled={cancelling}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-cancel-btn"
                >
                  {cancelling ? 'Reassigning…' : 'Confirm Cancel'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Distribute Dialog */}
      <DistributeDialog open={!!distFile} onOpenChange={(v) => !v && setDistFile(null)} file={distFile} onDone={fetchData} />
    </div>
  );
}
